import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useRef, useCallback } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
const textAreaVariants = cva('w-full rounded-lg border p-4 shadow-sm transition-colors focus:outline-none focus:ring-2', {
    variants: {
        variant: {
            default: [
                'border-gray-300 text-gray-600',
                'hover:border-interactive-emerald-flash',
                'focus:ring-interactive-emerald-flash',
                'placeholder:text-gray-400',
            ],
            error: [
                'border-interactive-coral-red text-gray-600',
                'focus:ring-interactive-coral-red',
                'placeholder:text-gray-400',
            ],
        },
    },
    defaultVariants: {
        variant: 'default',
    },
});
export const TextArea = React.forwardRef(({ className, variant, value, onChange, label, maxLength, autoResize = false, disabled = false, showCount = false, onClear, error = false, ...props }, ref) => {
    const internalRef = useRef(null);
    const textareaRef = (ref || internalRef);
    const [showClearButton, setShowClearButton] = React.useState(false);
    useEffect(() => {
        setShowClearButton(!!value && !disabled && !!onClear);
    }, [value, disabled, onClear]);
    const adjustHeight = useCallback(() => {
        if (!autoResize || !textareaRef.current)
            return;
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }, [autoResize, textareaRef]);
    useEffect(() => {
        if (autoResize) {
            requestAnimationFrame(adjustHeight);
        }
    }, [value, autoResize, adjustHeight]);
    const handleChange = (e) => {
        const newValue = e.target.value;
        if (maxLength !== undefined && newValue.length > maxLength) {
            // Only update if deleting characters when over limit
            if (newValue.length < (value?.length || 0)) {
                onChange(newValue);
            }
            return;
        }
        onChange(newValue);
    };
    const handleClear = () => {
        if (onClear) {
            onClear();
        }
    };
    const isOverLimit = maxLength !== undefined && typeof value === 'string' && value.length > maxLength;
    const finalVariant = error || isOverLimit ? 'error' : variant;
    return (_jsxs("div", { className: cn('text-area-wrapper w-full', className), children: [(label || (showCount && maxLength)) && (_jsxs("div", { className: "mb-1 flex items-center justify-between", children: [label && _jsx("label", { className: "block text-sm font-medium text-gray-600", children: label }), showCount && maxLength && (_jsxs("span", { className: cn('text-xs', isOverLimit ? 'text-interactive-coral-red' : 'text-gray-400'), children: [value.length, "/", maxLength] }))] })), _jsxs("div", { className: "relative", children: [_jsx("textarea", { ref: textareaRef, className: cn(textAreaVariants({ variant: finalVariant }), disabled && 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'), value: value, onChange: handleChange, disabled: disabled, maxLength: maxLength, ...props }), showClearButton && (_jsx("button", { type: "button", className: "absolute right-3 top-3 text-gray-400 transition-colors hover:text-gray-600 focus:outline-none", onClick: handleClear, "aria-label": "Clear text", children: _jsx("svg", { xmlns: "http://www.w3.org/2000/svg", className: "h-5 w-5", viewBox: "0 0 20 20", fill: "currentColor", children: _jsx("path", { fillRule: "evenodd", d: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z", clipRule: "evenodd" }) }) }))] }), isOverLimit && (_jsx("p", { className: "mt-1 text-xs text-interactive-coral-red", children: "Maximum character limit exceeded" }))] }));
});
TextArea.displayName = 'TextArea';
export default TextArea;
//# sourceMappingURL=TextArea.js.map